import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

interface TeamMember {
  name: string;
  position: string;
  bio: string;
  imageUrl: string;
}

interface CompanyValue {
  title: string;
  description: string;
  iconClass: string;
}

interface Milestone {
  year: number;
  title: string;
  description: string;
}

@Component({
  selector: 'app-about',
  standalone: true,
  imports: [CommonModule],
  template: `
    <section class="about-page">
      <div class="container">
        <header class="page-header">
          <h1>About Industry Intelligence</h1>
          <p class="subtitle">Empowering businesses with actionable insights since 2005</p>
        </header>

        <div class="mission-section">
          <h2>Our Mission</h2>
          <p class="mission-statement">
            To revolutionize how businesses access, analyze, and act on industry intelligence,
            enabling informed decisions that drive growth and innovation in the global market.
          </p>
        </div>

        <div class="values-section">
          <h2>Our Values</h2>
          <div class="values-grid">
            <div class="value-card" *ngFor="let value of companyValues">
              <i [class]="value.iconClass"></i>
              <h3>{{ value.title }}</h3>
              <p>{{ value.description }}</p>
            </div>
          </div>
        </div>

        <div class="history-section">
          <h2>Our Journey</h2>
          <div class="timeline">
            <div class="milestone" *ngFor="let milestone of milestones">
              <div class="year">{{ milestone.year }}</div>
              <div class="milestone-content">
                <h3>{{ milestone.title }}</h3>
                <p>{{ milestone.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="team-section">
          <h2>Leadership Team</h2>
          <div class="team-grid">
            <div class="team-member" *ngFor="let member of teamMembers">
              <div class="member-image">
                <img [src]="member.imageUrl" [alt]="member.name">
              </div>
              <div class="member-info">
                <h3>{{ member.name }}</h3>
                <p class="position">{{ member.position }}</p>
                <p class="bio">{{ member.bio }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="stats-section">
          <div class="stat-card">
            <div class="stat-number">20K+</div>
            <div class="stat-label">Global Clients</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">50+</div>
            <div class="stat-label">Countries Served</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">18+</div>
            <div class="stat-label">Years of Excellence</div>
          </div>
          <div class="stat-card">
            <div class="stat-number">250+</div>
            <div class="stat-label">Team Members</div>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .about-page {
      padding: 4rem 0;
      background: var(--background-light);
    }

    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 0 1rem;
    }

    .page-header {
      text-align: center;
      margin-bottom: 4rem;
    }

    h1 {
      font-size: 2.5rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .subtitle {
      color: var(--text-secondary);
      font-size: 1.1rem;
    }

    section {
      margin-bottom: 4rem;
    }

    h2 {
      font-size: 2rem;
      color: var(--text-primary);
      margin-bottom: 2rem;
      text-align: center;
    }

    .mission-section {
      text-align: center;
      max-width: 800px;
      margin: 0 auto 4rem;
    }

    .mission-statement {
      font-size: 1.25rem;
      line-height: 1.6;
      color: var(--text-secondary);
    }

    .values-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
      gap: 2rem;
      margin-bottom: 4rem;
    }

    .value-card {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      text-align: center;
    }

    .value-card i {
      font-size: 2.5rem;
      color: var(--primary-color);
      margin-bottom: 1rem;
    }

    .value-card h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin-bottom: 1rem;
    }

    .value-card p {
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .timeline {
      position: relative;
      max-width: 800px;
      margin: 0 auto;
    }

    .timeline::before {
      content: '';
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      width: 2px;
      height: 100%;
      background: var(--primary-color);
    }

    .milestone {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
      margin-bottom: 2rem;
      position: relative;
    }

    .year {
      text-align: right;
      font-size: 1.5rem;
      font-weight: bold;
      color: var(--primary-color);
      padding-right: 2rem;
    }

    .milestone-content {
      padding-left: 2rem;
    }

    .milestone-content h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .milestone-content p {
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .team-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 2rem;
      margin-bottom: 4rem;
    }

    .team-member {
      background: white;
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }

    .member-image {
      width: 100%;
      height: 300px;
      overflow: hidden;
    }

    .member-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .member-info {
      padding: 1.5rem;
    }

    .member-info h3 {
      font-size: 1.25rem;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }

    .position {
      color: var(--primary-color);
      font-weight: 500;
      margin-bottom: 1rem;
    }

    .bio {
      color: var(--text-secondary);
      line-height: 1.5;
    }

    .stats-section {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 2rem;
      margin-top: 4rem;
    }

    .stat-card {
      background: white;
      padding: 2rem;
      border-radius: 8px;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      text-align: center;
    }

    .stat-number {
      font-size: 2.5rem;
      font-weight: bold;
      color: var(--primary-color);
      margin-bottom: 0.5rem;
    }

    .stat-label {
      color: var(--text-secondary);
      font-size: 1.1rem;
    }

    @media (max-width: 992px) {
      .timeline::before {
        left: 0;
      }

      .milestone {
        grid-template-columns: 1fr;
        gap: 1rem;
      }

      .year {
        text-align: left;
        padding-right: 0;
        padding-left: 2rem;
      }

      .milestone-content {
        padding-left: 2rem;
      }
    }

    @media (max-width: 768px) {
      .page-header {
        margin-bottom: 3rem;
      }

      h1 {
        font-size: 2rem;
      }

      h2 {
        font-size: 1.5rem;
      }

      .mission-statement {
        font-size: 1.1rem;
      }

      .stats-section {
        grid-template-columns: repeat(2, 1fr);
      }
    }

    @media (max-width: 576px) {
      .about-page {
        padding: 2rem 0;
      }

      .stats-section {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AboutComponent {
  companyValues: CompanyValue[] = [
    {
      title: 'Innovation',
      description: 'Continuously pushing boundaries to deliver cutting-edge solutions that transform how businesses operate.',
      iconClass: 'fas fa-lightbulb'
    },
    {
      title: 'Integrity',
      description: 'Maintaining the highest standards of transparency and ethical conduct in all our operations.',
      iconClass: 'fas fa-shield-alt'
    },
    {
      title: 'Excellence',
      description: 'Striving for exceptional quality in our products, services, and customer relationships.',
      iconClass: 'fas fa-star'
    },
    {
      title: 'Collaboration',
      description: 'Working together with clients and partners to achieve shared success and growth.',
      iconClass: 'fas fa-hands-helping'
    }
  ];

  milestones: Milestone[] = [
    {
      year: 2005,
      title: 'Company Founded',
      description: 'Established with a vision to revolutionize industry intelligence.'
    },
    {
      year: 2010,
      title: 'Global Expansion',
      description: 'Opened offices in New York and London, serving clients worldwide.'
    },
    {
      year: 2015,
      title: 'Platform Innovation',
      description: 'Launched our AI-powered analytics platform, transforming data insights.'
    },
    {
      year: 2020,
      title: 'Industry Leadership',
      description: 'Recognized as the leading provider of industry intelligence solutions.'
    },
    {
      year: 2023,
      title: 'Sustainable Future',
      description: 'Introduced ESG analytics and sustainable business intelligence tools.'
    }
  ];

  teamMembers: TeamMember[] = [
    {
      name: 'Sarah Chen',
      position: 'Chief Executive Officer',
      bio: 'With over 20 years of experience in technology and analytics, Sarah leads our global strategy and innovation initiatives.',
      imageUrl: 'assets/images/team/sarah-chen.jpg'
    },
    {
      name: 'Michael Rodriguez',
      position: 'Chief Technology Officer',
      bio: 'Michael brings extensive expertise in AI and machine learning, driving our technological advancement.',
      imageUrl: 'assets/images/team/michael-rodriguez.jpg'
    },
    {
      name: 'Emily Thompson',
      position: 'Chief Product Officer',
      bio: 'Emily oversees product development and innovation, ensuring our solutions meet evolving market needs.',
      imageUrl: 'assets/images/team/emily-thompson.jpg'
    },
    {
      name: 'David Kumar',
      position: 'Chief Data Officer',
      bio: 'David leads our data strategy and analytics capabilities, transforming raw data into actionable insights.',
      imageUrl: 'assets/images/team/david-kumar.jpg'
    }
  ];
} 